import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ShieldCheck, 
  Database, 
  Share2, 
  Lock, 
  Cookie, 
  UserCircle, 
  RefreshCw, 
  Mail,
  ChevronLeft,
  BellRing
} from 'lucide-react';

const PrivacyPolicy = () => {
  // Updated to today's date
  const lastUpdated = "May 11, 2026";

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8 font-sans">
      
      {/* Back Button */}
      <div className="max-w-4xl mx-auto mb-6">
        <Link 
          to="/" 
          className="inline-flex items-center text-emerald-600 hover:text-emerald-700 transition-colors font-medium"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back to Home
        </Link>
      </div>

      <div className="max-w-4xl mx-auto bg-white shadow-xl rounded-3xl overflow-hidden border border-slate-100">
        
        {/* Beautiful Header Section */}
        <div className="bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800 text-white py-16 px-8 text-center relative overflow-hidden">
          {/* Decorative background circles */}
          <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 rounded-full bg-white opacity-5 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-48 h-48 rounded-full bg-white opacity-5 blur-2xl"></div>
          
          <div className="relative z-10">
            <h1 className="text-4xl md:text-5xl font-extrabold mb-4 tracking-tight">Privacy Policy</h1>
            <p className="text-emerald-100 text-lg font-medium">uCart Marketplace • Last Updated: {lastUpdated}</p>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-8 md:p-12">
          <p className="text-slate-600 leading-relaxed text-lg mb-12">
            Welcome to <strong>uCart</strong>. We deeply respect your privacy and are committed to protecting your personal data. This Privacy Policy explains what information we collect, how we use it, and the steps we take to keep it entirely secure while you use our platform.
          </p>

          <div className="space-y-12">
            
            {/* Section 1 */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <div className="bg-emerald-100 p-2 rounded-lg text-emerald-600">
                  <Database className="w-6 h-6" />
                </div>
                1. Information We Collect
              </h2>
              <p className="text-slate-600 mb-3 pl-14 font-medium">When you use uCart, we may collect the following types of information:</p>
              <ul className="list-none space-y-3 text-slate-600 leading-relaxed pl-14">
                <li className="relative before:content-['•'] before:absolute before:-left-5 before:text-emerald-500 before:font-bold before:text-xl">
                  <strong>Personal Details:</strong> Your name, email address, phone number, gender, and university when you register an account.
                </li>
                <li className="relative before:content-['•'] before:absolute before:-left-5 before:text-emerald-500 before:font-bold before:text-xl">
                  <strong>Seller Information:</strong> If you are a seller, we collect your bank details, store name, and verification documents (like a Student ID or NIN) to ensure marketplace safety.
                </li>
                <li className="relative before:content-['•'] before:absolute before:-left-5 before:text-emerald-500 before:font-bold before:text-xl">
                  <strong>Transaction Data:</strong> Details of your orders, purchases, delivery addresses, and payout history. We do not store raw credit card numbers; these are handled securely by Paystack.
                </li>
              </ul>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <div className="bg-emerald-100 p-2 rounded-lg text-emerald-600">
                  <BellRing className="w-6 h-6" />
                </div>
                2. How We Use Your Information
              </h2>
              <p className="text-slate-600 mb-3 pl-14 font-medium">We strictly use your data to make uCart work smoothly for you. Specifically, we use it to:</p>
              <ul className="list-none space-y-3 text-slate-600 leading-relaxed pl-14">
                <li className="relative before:content-['✓'] before:absolute before:-left-6 before:text-emerald-500 before:font-bold">
                  Create and manage your buyer or seller account.
                </li>
                <li className="relative before:content-['✓'] before:absolute before:-left-6 before:text-emerald-500 before:font-bold">
                  Process your orders, payments, and seller withdrawals securely.
                </li>
                <li className="relative before:content-['✓'] before:absolute before:-left-6 before:text-emerald-500 before:font-bold">
                  Send you important updates, such as order confirmations and password resets.
                </li>
                <li className="relative before:content-['✓'] before:absolute before:-left-6 before:text-emerald-500 before:font-bold">
                  Track referral program earnings and verify platform compliance.
                </li>
              </ul>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <div className="bg-emerald-100 p-2 rounded-lg text-emerald-600">
                  <Share2 className="w-6 h-6" />
                </div>
                3. Sharing Your Information
              </h2>
              <p className="text-slate-600 leading-relaxed pl-14 mb-3">
                We do not sell your personal data to advertisers. However, we may share your data in the following necessary situations:
              </p>
              <ul className="list-none space-y-3 text-slate-600 leading-relaxed pl-14">
                <li className="relative before:content-['•'] before:absolute before:-left-5 before:text-emerald-500 before:font-bold before:text-xl">
                  <strong>With Sellers/Buyers:</strong> When an order is placed, we share the buyer's name, phone number, and pickup address with the seller, and vice versa, so the physical exchange can happen.
                </li>
                <li className="relative before:content-['•'] before:absolute before:-left-5 before:text-emerald-500 before:font-bold before:text-xl">
                  <strong>With Service Providers:</strong> Trusted third parties like Paystack (for payments) and EmailJS (for order notifications) receive the minimum data required to perform their jobs.
                </li>
                <li className="relative before:content-['•'] before:absolute before:-left-5 before:text-emerald-500 before:font-bold before:text-xl">
                  <strong>Legal Requirements:</strong> We may disclose information if required by Nigerian law or to protect the rights, property, or safety of uCart and its users.
                </li>
              </ul>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <div className="bg-emerald-100 p-2 rounded-lg text-emerald-600">
                  <Lock className="w-6 h-6" />
                </div>
                4. Data Security
              </h2>
              <p className="text-slate-600 leading-relaxed pl-14">
                We implement strict security measures to protect your personal information. Your account is secured by Google Firebase authentication. However, remember that no method of transmission over the internet is 100% secure, and we cannot guarantee absolute absolute security against advanced cyber threats. Please keep your password safe.
              </p>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <div className="bg-emerald-100 p-2 rounded-lg text-emerald-600">
                  <Cookie className="w-6 h-6" />
                </div>
                5. Cookies and Tracking
              </h2>
              <p className="text-slate-600 leading-relaxed pl-14">
                uCart uses local storage and cookies to keep you logged in, save your shopping cart items, and track referral codes smoothly across pages. You can control or delete cookies through your browser settings, but doing so may affect how the marketplace functions for you.
              </p>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <div className="bg-emerald-100 p-2 rounded-lg text-emerald-600">
                  <UserCircle className="w-6 h-6" />
                </div>
                6. Your Privacy Rights
              </h2>
              <p className="text-slate-600 mb-3 pl-14 font-medium">As a user, you have the right to:</p>
              <ul className="list-none space-y-3 text-slate-600 leading-relaxed pl-14">
                <li className="relative before:content-['•'] before:absolute before:-left-5 before:text-emerald-500 before:font-bold before:text-xl">
                  Access and update your personal information through your Profile dashboard.
                </li>
                <li className="relative before:content-['•'] before:absolute before:-left-5 before:text-emerald-500 before:font-bold before:text-xl">
                  Request the deletion of your account and personal data (subject to pending transactions or legal obligations).
                </li>
              </ul>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <div className="bg-emerald-100 p-2 rounded-lg text-emerald-600">
                  <RefreshCw className="w-6 h-6" />
                </div>
                7. Changes to this Policy
              </h2>
              <p className="text-slate-600 leading-relaxed pl-14">
                We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. When we make significant changes, we will notify you by updating the "Last Updated" date at the top of this page.
              </p>
            </section>

          </div>

          {/* Contact Footer Inside Card */}
          <div className="mt-16 pt-8 border-t border-slate-200 bg-slate-50 -mx-8 md:-mx-12 -mb-8 md:-mb-12 px-8 md:px-12 py-10">
            <h3 className="font-bold text-xl text-slate-800 mb-4 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              Privacy Inquiries
            </h3>
            <p className="text-slate-600 mb-2">
              If you have any questions, concerns, or requests regarding your data and this Privacy Policy, please contact us at:
            </p>
            <a href="mailto:hello.ibuyunn@gmail.com" className="text-emerald-600 font-bold text-lg hover:underline flex items-center gap-2">
              <Mail className="w-5 h-5" /> hello.ibuyunn@gmail.com
            </a>
          </div>
        </div>
      </div>

      {/* Page Footer */}
      <div className="text-center mt-8 text-sm text-slate-500 font-medium">
        © {new Date().getFullYear()} uCart Marketplace. All Rights Reserved.
      </div>
    </div>
  );
};

export default PrivacyPolicy;